import { AddOutlined } from '@mui/icons-material';
import { Alert, Box, Button, ThemeProvider } from '@mui/material';
import type { FormikHelpers } from 'formik';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import ConfirmationDialog from 'components/v2/common/confirmationDialog';
import PageHeader from 'components/v2/common/pageHeader';
import PermissionsTable from 'views/v2/permissions/components/permissionsTable';
import PermissionUserForm from 'views/v2/permissions/components/permissionUserForm';

import api, { type ImmigrationProfessional } from 'api';
import { ACTIONS } from 'constant';
import useModalObj from 'hooks/useModalObj';
import { setAlert } from 'redux/alert';
import type { AppDispatch } from 'redux/store';
import appThemeV2 from 'theme/themeV2';
import type { PermissionUserFormInterface } from 'types';
import { getErrorMessage } from 'utils/v2/errorHelper';
import { permissionUserFormDefaultValues } from 'utils/validations/permissions';

export default function PermissionsPage() {
    const dispatch = useDispatch<AppDispatch>();
    const { modalObject, setModalObject, onResetModalObj } = useModalObj<ImmigrationProfessional>();

    const [users, setUsers] = useState<ImmigrationProfessional[]>([]);
    const [loading, setLoading] = useState(true);

    const loadUsers = useCallback(async () => {
        setLoading(true);
        try {
            const response = await api.listImmigrationProfessionals.get();
            if (response.status === 200) {
                setUsers(response.data || []);
            }
        } catch (error) {
            dispatch(
                setAlert({
                    message: getErrorMessage(error, 'Failed to load collaborators'),
                    type: 'error',
                })
            );
        } finally {
            setLoading(false);
        }
    }, [dispatch]);

    useEffect(() => {
        void loadUsers();
    }, [loadUsers]);

    const handleAction = (action: ACTIONS, data?: ImmigrationProfessional) => {
        switch (action) {
            case ACTIONS.CREATE:
                setModalObject((prev) => ({
                    ...prev,
                    action,
                    title: 'Invite collaborator',
                    data: null,
                    disabled: false,
                    loading: false,
                }));
                break;
            case ACTIONS.EDIT:
                if (!data) return;
                setModalObject((prev) => ({
                    ...prev,
                    action,
                    title: 'Edit collaborator',
                    data,
                    disabled: false,
                    loading: false,
                }));
                break;
            case ACTIONS.DELETE:
                if (!data) return;
                setModalObject((prev) => ({
                    ...prev,
                    action,
                    data,
                    title: 'Delete collaborator?',
                    content: `Are you sure you want to delete ${data.firstname || ''} ${
                        data.lastname || ''
                    }? This action cannot be undone.`
                        .replace(/\s+/g, ' ')
                        .trim(),
                    disabled: false,
                    loading: false,
                }));
                break;
            case ACTIONS.RESEND:
                if (!data) return;
                setModalObject((prev) => ({
                    ...prev,
                    action,
                    title: 'Resend invitation?',
                    content: `Resend the invitation email to ${data.email}?`,
                    data: data,
                    disabled: false,
                    loading: false,
                }));
                break;
            default:
                break;
        }
    };

    const handleFormSubmit = async (
        values: PermissionUserFormInterface,
        _helpers: FormikHelpers<PermissionUserFormInterface>
    ) => {
        const isEdit = modalObject.action === ACTIONS.EDIT;
        setModalObject((prev) => ({ ...prev, disabled: true, loading: true }));

        const payload = {
            firstname: values.firstname.trim(),
            lastname: values.lastname.trim(),
            middlename: (values.middlename || '')?.trim(),
            email: values.email.trim(),
            phone_number: (values.phone_number || '')?.trim(),
            role: values.role,
        };

        try {
            if (isEdit && modalObject.data?.id) {
                await api.immigrationProfessional.put(modalObject.data.id, payload);
                dispatch(
                    setAlert({
                        message: 'Collaborator updated successfully',
                        type: 'success',
                    })
                );
            } else {
                await api.immigrationProfessional.post(payload);
                dispatch(
                    setAlert({
                        message: 'Invitation sent successfully',
                        type: 'success',
                    })
                );
            }
            onResetModalObj();
            await loadUsers();
        } catch (error) {
            setModalObject((prev) => ({ ...prev, disabled: false, loading: false }));
            dispatch(
                setAlert({
                    message: getErrorMessage(
                        error,
                        isEdit ? 'Failed to update collaborator' : 'Failed to invite collaborator'
                    ),
                    type: 'error',
                })
            );
        }
    };

    const handleConfirmDelete = async () => {
        if (!modalObject.data?.id) return;

        try {
            setModalObject((prev) => ({ ...prev, loading: true }));
            await api.immigrationProfessional.deleteProfessional(String(modalObject.data.id));
            dispatch(setAlert({ message: 'Collaborator deleted', type: 'success' }));
            onResetModalObj();
            await loadUsers();
        } catch (error) {
            setModalObject((prev) => ({ ...prev, loading: false }));
            dispatch(
                setAlert({
                    message: getErrorMessage(error, 'Failed to delete collaborator'),
                    type: 'error',
                })
            );
        }
    };

    const handleConfirmResend = async () => {
        if (!modalObject.data?.id) return;

        try {
            setModalObject((prev) => ({ ...prev, loading: true, disabled: true }));
            const response = await api.sendInvitation.sendInvitation(modalObject.data.id);
            if (response.status === 200 || response.status === 201) {
                dispatch(
                    setAlert({
                        message: 'Invitation email sent successfully',
                        type: 'success',
                    })
                );
            }
            onResetModalObj();
        } catch (error) {
            setModalObject((prev) => ({ ...prev, loading: false, disabled: false }));
            dispatch(
                setAlert({
                    message: getErrorMessage(error, 'Failed to resend invitation'),
                    type: 'error',
                })
            );
        }
    };

    return (
        <ThemeProvider theme={appThemeV2}>
            <Box sx={{ width: '100%' }}>
                <PageHeader
                    title="permissions"
                    description="Manage team collaborators, roles, and invitations."
                >
                    <Button
                        variant="contained"
                        startIcon={<AddOutlined />}
                        onClick={() => handleAction(ACTIONS.CREATE)}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        Invite collaborator
                    </Button>
                </PageHeader>

                {!loading && users.length > 0 ? (
                    <Alert severity="info" sx={{ mb: 2.5, borderRadius: 2 }}>
                        Invited users receive an email to join your organization. You can resend the
                        invite while their status is Invited.
                    </Alert>
                ) : null}

                <PermissionsTable
                    users={users}
                    loading={loading}
                    onAction={(action, user) => handleAction(action, user)}
                />

                {modalObject.action === ACTIONS.CREATE || modalObject.action === ACTIONS.EDIT ? (
                    <PermissionUserForm
                        mode={modalObject.action === ACTIONS.EDIT ? 'edit' : 'create'}
                        initialValues={modalObject.data || permissionUserFormDefaultValues}
                        loading={modalObject.loading || modalObject.disabled}
                        onClose={onResetModalObj}
                        onSubmit={handleFormSubmit}
                    />
                ) : null}

                {modalObject.action === ACTIONS.DELETE ? (
                    <ConfirmationDialog
                        title={modalObject.title}
                        content={modalObject.content}
                        loading={modalObject.loading}
                        onClose={onResetModalObj}
                        onConfirm={handleConfirmDelete}
                    />
                ) : null}

                {modalObject.action === ACTIONS.RESEND ? (
                    <ConfirmationDialog
                        title={modalObject.title}
                        content={modalObject.content}
                        loading={modalObject.loading}
                        onClose={onResetModalObj}
                        onConfirm={handleConfirmResend}
                    />
                ) : null}
            </Box>
        </ThemeProvider>
    );
}
